import { useState } from "react";
import { Checkbox, Dialog, PrimaryButton, QuietButton, Select, TextArea, TextInput } from "./controls";
import type { ScheduleBlock, Technician } from "../lib/dispatch";
import {
  defaultPaidForKind,
  eventDateBounds,
  eventRangeIso,
  TECHNICIAN_EVENT_KINDS,
  TECHNICIAN_EVENT_LABELS,
  technicianEventLabel,
  type TechnicianEventKind,
} from "../lib/technician-events";

type Save = (path: string, method: "POST" | "PATCH" | "PUT", body: unknown) => Promise<unknown>;

const KIND_OPTIONS = TECHNICIAN_EVENT_KINDS.map((value) => ({
  value,
  label: TECHNICIAN_EVENT_LABELS[value],
}));

export function TechnicianEventDialog({
  technicians,
  timezone,
  date,
  event,
  defaultTechnicianId = "",
  save,
  onClose,
}: {
  technicians: Technician[];
  timezone: string;
  date: string;
  event?: ScheduleBlock | null;
  defaultTechnicianId?: string;
  save: Save;
  onClose: () => void;
}) {
  const editing = Boolean(event);
  const bounds = event ? eventDateBounds(event, timezone) : null;
  const [kind, setKind] = useState<TechnicianEventKind>(
    event && TECHNICIAN_EVENT_KINDS.includes(event.kind as TechnicianEventKind)
      ? (event.kind as TechnicianEventKind)
      : "pto",
  );
  const [technicianId, setTechnicianId] = useState(event?.technicianId ?? defaultTechnicianId);
  const [startDate, setStartDate] = useState(bounds?.startDate ?? date);
  const [endDate, setEndDate] = useState(bounds?.endDate ?? date);
  const [allDay, setAllDay] = useState(bounds?.allDay ?? true);
  const [startTime, setStartTime] = useState(bounds?.startTime && bounds.startTime !== "00:00" ? bounds.startTime : "08:00");
  const [endTime, setEndTime] = useState(bounds?.endTime && bounds.endTime !== "00:00" ? bounds.endTime : "17:00");
  const [paid, setPaid] = useState(event ? Boolean(event.paid) : defaultPaidForKind("pto"));
  const [title, setTitle] = useState(event?.title ?? TECHNICIAN_EVENT_LABELS.pto);
  const [note, setNote] = useState(event?.note ?? "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const technicianName = technicians.find((technician) => technician.id === technicianId)?.name;

  const setKindAndDefaults = (next: string) => {
    const typed = next as TechnicianEventKind;
    setKind(typed);
    if (!title.trim() || Object.values(TECHNICIAN_EVENT_LABELS).includes(title)) {
      setTitle(TECHNICIAN_EVENT_LABELS[typed]);
    }
    if (!editing) setPaid(defaultPaidForKind(typed));
  };

  const persist = async (body: Record<string, unknown>, method: "POST" | "PATCH", path: string) => {
    setBusy(true);
    setError("");
    try {
      await save(path, method, body);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That block did not save.");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!technicianId) {
      setError("Choose a technician.");
      return;
    }
    if (endDate < startDate) {
      setError("The last day cannot be before the first day.");
      return;
    }
    let range: ReturnType<typeof eventRangeIso>;
    try {
      range = eventRangeIso({ startDate, endDate, startTime, endTime, allDay, timezone });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Check the dates and times.");
      return;
    }
    if (range.endsAt <= range.startsAt) {
      setError("The end must be after the start.");
      return;
    }
    await persist(
      {
        technicianId,
        kind,
        paid,
        title: title.trim() || technicianEventLabel(kind),
        note: note.trim() || null,
        ...range,
      },
      editing ? "PATCH" : "POST",
      editing ? `/api/v1/workspace/dispatch/blocks/${event!.id}` : "/api/v1/workspace/dispatch/blocks",
    );
  };

  return (
    <Dialog
      title={editing ? technicianEventLabel(event!.kind) : "Block"}
      subtitle={editing ? technicianName : "Blocks that technician for booking and dispatch."}
      onClose={onClose}
      width="max-w-lg"
    >
      <div className="space-y-4">
        <Select label="Type" value={kind} onChange={setKindAndDefaults} options={KIND_OPTIONS} />
        <Select
          required
          label="Technician"
          value={technicianId}
          onChange={setTechnicianId}
          placeholder="Choose technician"
          options={technicians.map((technician) => ({ value: technician.id, label: technician.name }))}
        />
        <Checkbox checked={paid} onChange={setPaid}>
          Paid
        </Checkbox>
        <div className="grid gap-3 sm:grid-cols-2">
          <TextInput label="Starts" type="date" value={startDate} onChange={(change) => setStartDate(change.target.value)} />
          <TextInput label="Ends" type="date" value={endDate} onChange={(change) => setEndDate(change.target.value)} />
        </div>
        <Checkbox checked={allDay} onChange={setAllDay}>
          All day
        </Checkbox>
        {!allDay && (
          <div className="grid gap-3 sm:grid-cols-2">
            <TextInput label="Start time" type="time" value={startTime} onChange={(change) => setStartTime(change.target.value)} />
            <TextInput label="End time" type="time" value={endTime} onChange={(change) => setEndTime(change.target.value)} />
          </div>
        )}
        <TextInput label="Label" value={title} onChange={(change) => setTitle(change.target.value)} />
        <TextArea label="Note" value={note} onChange={(change) => setNote(change.target.value)} rows={2} />
        {error && (
          <p role="alert" className="text-[13px] font-semibold text-[var(--accent)]">
            {error}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <PrimaryButton type="button" disabled={busy} onClick={() => void submit()}>
            {busy ? "Saving…" : editing ? "Save changes" : "Save"}
          </PrimaryButton>
          {editing && (
            <QuietButton
              type="button"
              disabled={busy}
              onClick={() => void persist({ status: "cancelled" }, "PATCH", `/api/v1/workspace/dispatch/blocks/${event!.id}`)}
            >
              Cancel block
            </QuietButton>
          )}
        </div>
      </div>
    </Dialog>
  );
}
